import { useRouter } from 'next/router';
import { MouseEvent, useCallback, useState } from 'react';
import { useTheme } from 'styled-components';

import ThumbsDown from '~components/Icon/ThumbsDown';
import ThumbsUp from '~components/Icon/ThumbsUp';
import View from '~components/Icon/View';
import { WEB_URL } from '~constant/env';
import {
  ReactionType,
  useJobdomDislikeMutation,
  useJobdomLikeMutation,
} from '~hooks/query/jobdom/useJobdom';
import useGetMyInfoQuery from '~hooks/query/user/useMyInfoQuery';
import useMoveOneIdPage from '~hooks/useMoveOneIdPage';
import { JobdomPost } from '~types/jobdom';
import getOneIdUrl from '~utils/common/getOneIdUrl';
import { getJobdomViewCount } from '~utils/common/jobdomViewCount';
import { setGtmDataLayer } from '~utils/common/thirdPartyEvent';

import * as S from './style';

interface Props extends Omit<JobdomPost, 'id' | 'name'> {
  contentId: number;
  companyId: number;
  PK_NM_HASH: string;
  companyName: string;
  author: string;
  onClickReaction: () => void;
}

export default function JobdomItem({
  contentId,
  companyId,
  PK_NM_HASH,
  companyName,
  author,
  subject,
  createdAt,
  likesCount,
  unlikesCount,
  viewCount,
  commentCount,
  onClickReaction,
}: Props) {
  const theme = useTheme();
  const router = useRouter();
  const moveOneIdPage = useMoveOneIdPage();
  const { isActiveUser } = useGetMyInfoQuery();
  const { mutate: handleLike } = useJobdomLikeMutation(onClickReaction);
  const { mutate: handleDislike } = useJobdomDislikeMutation(onClickReaction);
  const [clickedLike, setClickedLike] = useState<boolean>();
  const [clickedDislike, setClickedDislike] = useState<boolean>();

  const getReactionIconColor = (value?: boolean) =>
    value ? theme.colors.gray900 : theme.colors.gray500;

  const handleClickItem = (url: string) => {
    const viewCount = getJobdomViewCount() as number;
    const isUnavailable = !isActiveUser && viewCount >= 1;

    setGtmDataLayer('company__board__content__click', {
      companyName,
      companyHash: PK_NM_HASH,
      contentId,
      landingUrl: isUnavailable
        ? getOneIdUrl({
            beforeUrl: `${WEB_URL}${router.asPath}`,
          })
        : `${WEB_URL}${url}`,
    });

    if (isUnavailable) {
      moveOneIdPage({ beforeUrl: url, messageKey: 'kreditjob_company_bookmark_title' });

      return;
    }

    router.push(url);
  };

  const handleClickLike = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      let type: ReactionType = 'minus';

      if (!clickedLike) {
        type = 'plus';
      }

      setClickedLike(type === 'plus');
      handleLike({ jobdomNumber: contentId, type });
    },
    [contentId, clickedLike, handleLike],
  );

  const handleClickDislike = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      let type: ReactionType = 'minus';

      if (!clickedDislike) {
        type = 'plus';
      }

      setClickedDislike(type === 'plus');
      handleDislike({ jobdomNumber: contentId, type });
    },
    [contentId, clickedDislike, handleDislike],
  );

  return (
    <S.Wrapper>
      <S.Anchor onClick={() => handleClickItem(`/board/${contentId}`)}>
        <S.Content>
          {subject}
          {commentCount > 0 && <S.Comment typography='bold120_C2'>{commentCount}</S.Comment>}
        </S.Content>
        <S.InfoWrapper>
          <S.ItemGroup>
            <S.InfoItem>
              <S.IconWrapper onClick={handleClickLike} as='button'>
                <S.ThumbsUpWrapper>
                  <ThumbsUp width={14} height={13} color={getReactionIconColor(clickedLike)} />
                </S.ThumbsUpWrapper>
              </S.IconWrapper>
              <div>{likesCount}</div>
            </S.InfoItem>
            <S.InfoItem>
              <S.IconWrapper onClick={handleClickDislike} as='button'>
                <S.ThumbsDownWrapper>
                  <ThumbsDown width={14} height={13} color={getReactionIconColor(clickedDislike)} />
                </S.ThumbsDownWrapper>
              </S.IconWrapper>
              <div>{unlikesCount}</div>
            </S.InfoItem>
            <S.InfoItem>
              <S.IconWrapper>
                <View width={14} height={10} color={theme.colors.gray500} />
              </S.IconWrapper>
              <div>{viewCount}</div>
            </S.InfoItem>
          </S.ItemGroup>
          <S.ItemGroup align='right'>
            <S.InfoItem>{author}</S.InfoItem>
            <S.InfoItem>{createdAt}</S.InfoItem>
          </S.ItemGroup>
        </S.InfoWrapper>
      </S.Anchor>
    </S.Wrapper>
  );
}
